import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Shield, RefreshCw, Download, Globe, Users,
  CheckCircle2, Circle, Mail, Trash2, Send, Filter,
} from "lucide-react";

/*  Admin dashboard for the NYE Global Mystery Night reservations.
    Admin-token gated via `wasntme_admin_token` in localStorage (same key
    used by /admin). Filters: country / warmup-sent / main-sent / card-sent.
    Actions: refresh, download CSV, trigger backfill, purge test traffic.
*/

const countryToFlag = (code) => {
  const c = (code || "").toUpperCase();
  if (c.length !== 2 || !/^[A-Z]{2}$/.test(c)) return "🌐";
  return String.fromCodePoint(
    0x1F1E6 + c.charCodeAt(0) - 65,
    0x1F1E6 + c.charCodeAt(1) - 65,
  );
};

const Pill = ({ on }) => (
  on ? <CheckCircle2 size={14} className="text-[#4ade80]" />
     : <Circle size={14} className="text-[#5a5050]" />
);

const AdminNye = () => {
  const [token, setToken] = useState(localStorage.getItem("wasntme_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [captionPerf, setCaptionPerf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ country: "", warmup_sent: "", main_sent: "", card_sent: "" });
  const [acting, setActing] = useState("");

  const headers = { headers: { "x-admin-token": token } };

  const loadWithToken = async (tkn) => {
    if (!tkn) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const h = { headers: { "x-admin-token": tkn } };
      const [r1, r2, r3, r4] = await Promise.all([
        api.get(`/admin/nye/reservations?${params.toString()}`, h),
        api.get("/nye/stats"),
        api.get("/admin/nye/analytics", h),
        api.get("/admin/nye/caption-performance", h),
      ]);
      setData(r1.data);
      setStats(r2.data);
      setAnalytics(r3.data);
      setCaptionPerf(r4.data);
      setAuthed(true);
    } catch (err) {
      setAuthed(false);
      if (err?.response?.status === 401) {
        toast.error("Invalid admin token.");
        localStorage.removeItem("wasntme_admin_token");
        setToken("");
      } else {
        toast.error(err?.response?.data?.detail || "Load failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const load = () => loadWithToken(token);

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unlock = (e) => {
    e.preventDefault();
    const t = tokenInput;
    localStorage.setItem("wasntme_admin_token", t);
    setToken(t);
    setTokenInput("");
    loadWithToken(t);
  };

  const sendCards = async () => {
    if (!window.confirm("Send a crew-variant card to every reservation that hasn't been sent one? Test emails are automatically skipped.")) return;
    setActing("backfill");
    try {
      const r = await api.post("/admin/nye/send-pending-cards?variant=crew", {}, headers);
      toast.success(`Sent ${r.data.sent} · skipped ${r.data.skipped_test + r.data.skipped_already}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backfill failed");
    } finally {
      setActing("");
    }
  };

  const purgeTest = async () => {
    if (!window.confirm("Delete every reservation that looks like dev/test traffic (@example.com, pytest-*, smoke, etc)? Counter resets to the highest remaining detective number.")) return;
    setActing("purge");
    try {
      const r = await api.post("/admin/nye/purge-test-reservations", {}, headers);
      toast.success(`Purged ${r.data.deleted} · counter now ${r.data.counter_reset_to}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Purge failed");
    } finally {
      setActing("");
    }
  };

  const downloadCsv = () => {
    if (!data?.rows?.length) return;
    const headersRow = ["detective_number", "email", "name", "country", "created_at",
                        "warmup_sent", "main_sent", "card_sent", "share_token"];
    const rows = data.rows.map((r) =>
      headersRow.map((k) => {
        const v = r[k];
        if (v == null) return "";
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      }).join(","),
    );
    const csv = [headersRow.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `nye-reservations-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  if (!authed) {
    return (
      <div className="grain min-h-screen flex items-center justify-center p-6">
        <form onSubmit={unlock} className="card-noir p-6 w-full max-w-sm"
          data-testid="admin-nye-unlock">
          <div className="flex items-center gap-2 mb-4">
            <Shield size={16} className="text-[#d4af37]" />
            <div className="font-display text-xl text-[#f4f0ec]">Admin — NYE</div>
          </div>
          <input type="password" placeholder="X-Admin-Token"
            value={tokenInput} onChange={(e) => setTokenInput(e.target.value)}
            className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-3 text-[#f4f0ec] text-sm mb-4 focus:outline-none focus:border-[#d4af37]"
            autoFocus data-testid="admin-nye-token-input" />
          <button type="submit" className="btn-primary w-full justify-center"
            data-testid="admin-nye-unlock-btn">
            Unlock
          </button>
          <Link to="/" className="block mt-4 text-center text-xs text-[#5a5050] hover:text-[#a39797]">
            ← back to the file
          </Link>
        </form>
      </div>
    );
  }

  const rows = data?.rows || [];

  return (
    <div className="grain min-h-screen" data-testid="admin-nye-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37]">
            <ArrowLeft size={16} /> back to the file
          </Link>
          <button onClick={() => {
            localStorage.removeItem("wasntme_admin_token");
            setToken(""); setAuthed(false);
          }} className="text-[11px] text-[#5a5050] hover:text-[#a39797]">
            lock
          </button>
        </div>

        <h1 className="font-display text-4xl text-[#f4f0ec] mb-2">NYE Reservations</h1>
        <div className="text-sm text-[#a39797] mb-6">
          <span className="text-[#d4af37]">Dec 31, 2026</span> · Global First Run — the proving night ·
          then <span className="text-[#a39797]">Jan 31, 2027</span> · The Ultimate Showdown
        </div>

        {/* Hero funnel analytics strip */}
        {analytics && (
          <div className="card-noir p-4 mb-6" data-testid="admin-nye-analytics">
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">HERO FUNNEL</div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
              <div><div className="text-[10px] text-[#a39797] tracking-widest">VIEWS</div>
                <div className="font-display text-2xl text-[#f4f0ec]">{analytics.hero_views}</div></div>
              <div><div className="text-[10px] text-[#a39797] tracking-widest">RESERVES</div>
                <div className="font-display text-2xl text-[#4ade80]">{analytics.reserves}</div></div>
              <div><div className="text-[10px] text-[#a39797] tracking-widest">RESERVE RATE</div>
                <div className="font-display text-2xl text-[#d4af37]">{analytics.reserve_rate_pct}%</div></div>
              <div><div className="text-[10px] text-[#a39797] tracking-widest">CAPTION COPIES</div>
                <div className="font-display text-2xl text-[#f4f0ec]">{analytics.caption_copies}</div></div>
              <div><div className="text-[10px] text-[#a39797] tracking-widest">CARD DOWNLOADS</div>
                <div className="font-display text-2xl text-[#f4f0ec]">{analytics.card_downloads}</div></div>
              <div><div className="text-[10px] text-[#a39797] tracking-widest">SHARE LINK COPIES</div>
                <div className="font-display text-2xl text-[#f4f0ec]">{analytics.share_link_copies}</div></div>
            </div>
          </div>
        )}

        {/* Caption winner-rotation performance */}
        {captionPerf && (
          <div className="card-noir p-4 mb-6" data-testid="admin-nye-caption-performance">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">
                CAPTION PERFORMANCE · {captionPerf.window_days}-DAY ROLLING
              </div>
              <div className="text-[10px] text-[#5a5050]">
                min {captionPerf.min_copies_rank} copies to rank · {captionPerf.pool_size} per language
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] tracking-widest text-[#a39797]">
                    <th className="text-left py-1 pr-3">LANG</th>
                    {Array.from({ length: captionPerf.pool_size }).map((_, i) => (
                      <th key={i} className="text-right py-1 px-2">#{i + 1}</th>
                    ))}
                    <th className="text-right py-1 pl-3">TOTAL</th>
                    <th className="text-right py-1 pl-3">WINNER</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(captionPerf.languages).map(([lang, info]) => (
                    <tr key={lang} className="border-t border-[#2d2424]/60"
                        data-testid={`caption-perf-row-${lang}`}>
                      <td className="py-1.5 pr-3 text-[#d4af37] font-mono uppercase">{lang}</td>
                      {Array.from({ length: captionPerf.pool_size }).map((_, i) => {
                        const n = info.counts[String(i)] || 0;
                        const isWinner = info.winner_idx === i;
                        return (
                          <td key={i} className={`text-right py-1.5 px-2 ${
                            isWinner ? "text-[#4ade80] font-bold" : n > 0 ? "text-[#f4f0ec]" : "text-[#5a5050]"
                          }`}>
                            {n}
                          </td>
                        );
                      })}
                      <td className="text-right py-1.5 pl-3 text-[#f4f0ec] font-bold">{info.total}</td>
                      <td className="text-right py-1.5 pl-3 text-[#4ade80] font-mono">
                        {info.winner_idx !== null ? `#${info.winner_idx + 1}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[10px] text-[#5a5050] mt-2">
              Caption #1 slot auto-fills with the top performer per language once it crosses {captionPerf.min_copies_rank} copies.
            </div>
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="card-noir p-4" data-testid="admin-nye-stat-total">
            <div className="text-[10px] tracking-widest text-[#a39797]">TOTAL RESERVED</div>
            <div className="font-display text-3xl text-[#d4af37] mt-1">
              {stats?.reserved_count ?? "—"}
            </div>
          </div>
          <div className="card-noir p-4">
            <div className="text-[10px] tracking-widest text-[#a39797]">COUNTRIES</div>
            <div className="font-display text-3xl text-[#d4af37] mt-1 flex items-center gap-2">
              <Globe size={20} /> {stats?.country_count ?? "—"}
            </div>
          </div>
          <div className="card-noir p-4">
            <div className="text-[10px] tracking-widest text-[#a39797]">WARMUP NUDGED</div>
            <div className="font-display text-3xl text-[#4ade80] mt-1">
              {rows.filter((r) => r.warmup_sent).length}
            </div>
          </div>
          <div className="card-noir p-4">
            <div className="text-[10px] tracking-widest text-[#a39797]">CARDS SENT</div>
            <div className="font-display text-3xl text-[#4ade80] mt-1">
              {rows.filter((r) => r.card_sent).length}
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="card-noir p-4 mb-5 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 text-[#a39797]">
            <Filter size={14} /> <span className="text-xs tracking-widest">FILTER</span>
          </div>
          <input placeholder="Country (US, BR…)"
            value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })}
            className="bg-[#0a0908] border border-[#2d2424] rounded px-2 py-1.5 text-sm text-[#f4f0ec] w-28"
            data-testid="admin-nye-filter-country" />
          {[
            ["warmup_sent", "Warmup"],
            ["main_sent", "Main"],
            ["card_sent", "Card"],
          ].map(([k, label]) => (
            <select key={k}
              value={filters[k]} onChange={(e) => setFilters({ ...filters, [k]: e.target.value })}
              className="bg-[#0a0908] border border-[#2d2424] rounded px-2 py-1.5 text-sm text-[#f4f0ec]"
              data-testid={`admin-nye-filter-${k}`}>
              <option value="">{label}: any</option>
              <option value="yes">{label}: yes</option>
              <option value="no">{label}: no</option>
            </select>
          ))}
          <button onClick={load} disabled={loading}
            className="btn-ghost text-sm" data-testid="admin-nye-refresh">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
          <div className="flex-1" />
          <button onClick={downloadCsv} disabled={!rows.length}
            className="btn-ghost text-sm" data-testid="admin-nye-csv">
            <Download size={14} /> CSV ({rows.length})
          </button>
          <button onClick={sendCards} disabled={acting !== ""}
            className="btn-ghost text-sm" data-testid="admin-nye-backfill">
            {acting === "backfill" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send pending cards
          </button>
          <button onClick={purgeTest} disabled={acting !== ""}
            className="btn-ghost text-sm text-[#ff6b6b]" data-testid="admin-nye-purge">
            {acting === "purge" ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Purge test
          </button>
        </div>

        {/* Table */}
        <div className="card-noir overflow-x-auto" data-testid="admin-nye-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] tracking-widest text-[#a39797] border-b border-[#2d2424]">
                <th className="text-left px-4 py-3">#</th>
                <th className="text-left px-4 py-3">EMAIL</th>
                <th className="text-left px-4 py-3">NAME</th>
                <th className="text-left px-4 py-3">COUNTRY</th>
                <th className="text-left px-4 py-3">CREATED</th>
                <th className="text-center px-2 py-3">WARMUP</th>
                <th className="text-center px-2 py-3">MAIN</th>
                <th className="text-center px-2 py-3">CARD</th>
                <th className="text-left px-4 py-3">SHARE</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={9} className="text-center text-[#5a5050] py-10"
                  data-testid="admin-nye-empty">
                  No reservations match these filters yet.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.email}
                  className="border-b border-[#2d2424]/50 hover:bg-[#1a1413]/30 transition-colors"
                  data-testid={`admin-nye-row-${r.detective_number}`}>
                  <td className="px-4 py-3 font-mono text-[#d4af37]">#{r.detective_number ?? "—"}</td>
                  <td className="px-4 py-3 text-[#f4f0ec]">{r.email}</td>
                  <td className="px-4 py-3 text-[#c7bebe]">{r.name || ""}</td>
                  <td className="px-4 py-3">
                    {r.country ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span>{countryToFlag(r.country)}</span>
                        <span className="font-mono text-[#f4f0ec]">{r.country}</span>
                      </span>
                    ) : <span className="text-[#5a5050]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-[#a39797] font-mono">
                    {r.created_at?.replace("T", " ").slice(0, 16) || ""}
                  </td>
                  <td className="text-center px-2 py-3"><Pill on={r.warmup_sent} /></td>
                  <td className="text-center px-2 py-3"><Pill on={r.main_sent} /></td>
                  <td className="text-center px-2 py-3"><Pill on={r.card_sent} /></td>
                  <td className="px-4 py-3">
                    {r.share_token ? (
                      <a href={`/?ref=NYE_${r.share_token}`} target="_blank" rel="noreferrer"
                        className="text-[11px] text-[#d4af37] hover:text-[#f4f0ec]">
                        /ref=NYE_{r.share_token.slice(0, 8)}…
                      </a>
                    ) : <span className="text-[#5a5050]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 text-[11px] text-[#5a5050] flex items-center justify-between">
            <span>Showing {data?.shown ?? 0} of {data?.total ?? 0}</span>
            <span className="flex items-center gap-1"><Users size={11} /> {rows.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNye;
